import { ThemeEvents, VariantUpdateEvent } from '@theme/events';
import { morph } from '@theme/morph';

class ProductInventory extends HTMLElement {
  connectedCallback() {
    const closestSection = this.closest('.shopify-section, dialog');
    closestSection?.addEventListener(ThemeEvents.variantUpdate, this.updateInventory);
  }

  disconnectedCallback() {
    const closestSection = this.closest('.shopify-section, dialog');
    closestSection?.removeEventListener(ThemeEvents.variantUpdate, this.updateInventory);
  }

  /**
   * Updates the inventory.
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  updateInventory = (event) => {
    if (event.detail.data.newProduct) {
      this.dataset.productId = event.detail.data.newProduct.id;
    } else if (event.target instanceof HTMLElement && event.target.dataset.productId !== this.dataset.productId) {
      return;
    }

    const newInventory = event.detail.data.html.querySelector('product-inventory');

    if (!newInventory) return;

    morph(this, newInventory, { childrenOnly: true });
  };
}

if (!customElements.get('product-inventory')) {
  customElements.define('product-inventory', ProductInventory);
}

class ProductInventoryLocations extends HTMLElement {
  connectedCallback() {
    const sku = this.dataset.sku;
    const closestSection = this.closest('.shopify-section, dialog');
    closestSection?.addEventListener(ThemeEvents.variantUpdate, this.updateInventoryLocations);

    this.apiProxy = (this.dataset && this.dataset.apiProxy) ? String(this.dataset.apiProxy).trim() : '';
    this.inventoryDataSource = this.dataset.inventoryDataSource;
    this.bindErpControls();
    this.refreshLocation(sku);
  }

  disconnectedCallback() {
    const closestSection = this.closest('.shopify-section, dialog');
    closestSection?.removeEventListener(ThemeEvents.variantUpdate, this.updateInventoryLocations);
  }

  async refreshLocation(sku) {
    let productInventoryLocationsCard = this.querySelector('.product-inventory-locations__card');
    if (this.inventoryDataSource === 'erp') {
      let searchInput = this.querySelector('.inventory-locations__search-input');
      if(searchInput) {
        searchInput.value = '';
      }
      productInventoryLocationsCard?.classList.toggle('inventory-locations__list--skeleton', true)
      try {
        await this.refreshErpInventory(sku);
      } finally {
        productInventoryLocationsCard?.classList.toggle('inventory-locations__list--skeleton', false)
      }
    } else {
      productInventoryLocationsCard?.classList.toggle('inventory-locations__list--skeleton', false)
    }
  }

  bindErpControls() {
    if (this.inventoryDataSource !== 'erp') return;
    if (this.dataset.erpControlsBound === 'true') return;
    this.dataset.erpControlsBound = 'true';

    this.currentSearchQuery = this.currentSearchQuery || '';
    this.onlyAvailable = Boolean(this.onlyAvailable);

    const searchInput = this.querySelector('.inventory-locations__search-input');
    const searchButton = this.querySelector('.inventory-locations__search-icon');
    const locateButton = this.querySelector('.inventory-locations__search-locate');
    const filterInput = this.querySelector('.inventory-locations__availability-toggle-input');

    searchInput?.addEventListener('input', () => {
      this.currentSearchQuery = searchInput.value || '';
      this.searchLocations(this.currentSearchQuery);
    });

    searchInput?.addEventListener('keydown', (event) => {
      if (event.key !== 'Enter') return;
      this.currentSearchQuery = searchInput.value || '';
      this.searchLocations(this.currentSearchQuery);
    });

    searchButton?.addEventListener('click', () => {
      this.currentSearchQuery = searchInput?.value || '';
      this.searchLocations(this.currentSearchQuery);
    });

    locateButton?.addEventListener('click', () => {
      this.sortByUserLocation();
    });

    filterInput?.addEventListener('change', () => {
      this.onlyAvailable = filterInput.checked;
      this.applyFilters();
    });
  }

  async sortByUserLocation() {
    if (!navigator.geolocation) {
      return;
    }

    navigator.geolocation.getCurrentPosition(
      async (position) => {
        try {
          let lat = position.coords.latitude;
          let lon = position.coords.longitude;
    
          let zipcode = localStorage.getItem('myzipcode');
    
          if(!zipcode) {
            try {
                await fetch(
                  `https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lon}`,
                  {
                    method: 'GET',
                    headers: {
                      Accept: 'application/json',
                    },
                    mode: 'cors',
                  }
                )
                .then((response) => response.json())
                .then((data) => {
                  zipcode = String(
                    data?.address?.postcode || ''
                  ).trim();
                  if(zipcode) {
                    localStorage.setItem('myzipcode', zipcode);
                  }
                })
                .catch((error) => {
                  console.error(error);
                });
            } catch (error) {
              console.error(
                '[product-inventory-locations] reverse geocode error',
                error
              );
            }
          }
    
          console.log('lat-lon', {lat, lon})

          await this.sortLocationsByGeocode({
            lat,
            lon,
            zipcode
          });
        } catch (error) {
          console.error(
            '[product-inventory-locations] geolocation error',
            error
          );
        }
      },
      (error) => {
        console.error(
          '[product-inventory-locations] geolocation error',
          error
        );
      }
    );
  }

  async refreshErpInventory(sku) {
    this.clearDataLocation();

    const dataErp = await this.loadingDataErp(sku);
    this.updateLable(dataErp);

    if (!dataErp || !Array.isArray(dataErp.mappedLocations)) {
      this.showNoLocationMessage();
      return;
    }

    this.renderDataLocation(dataErp);
  }

  async loadingDataErp(sku) {
    if (!sku || !this.apiProxy) {
      return null;
    }

    const storageKey = this.getStorageKey(sku);
    const dataLocation = this.getStorageItem(storageKey);

    if (dataLocation) {
      try {
        return JSON.parse(dataLocation);
      } catch (error) {
        this.removeStorageItem(storageKey);
      }
    }

    const erpInventoryUrl = this.apiProxy.replace(/\/?$/, '') + '/product/' + encodeURIComponent(sku) + '/erp';
    // const erpInventoryUrl = 'https://ari-app-shopify.arionline.com/api/location/stock';
    return await this.getData(erpInventoryUrl, storageKey);
  }

  updateLable(dataErp) {
    const iconInStock = this.querySelector('.product-inventory-locations__status-icon--in_stock');
    const labelInventoryLocationsAvailable = this.querySelector('.label-inventory-locations-available');
    const labelInventoryLocationsUnavailable = this.querySelector('.label-inventory-locations-unavailable');

    iconInStock?.classList.toggle('hidden', !dataErp?.in_shop);
    labelInventoryLocationsAvailable?.classList.toggle('hidden', !dataErp?.in_shop);

 
    labelInventoryLocationsUnavailable?.classList.toggle(
      'hidden',
      dataErp?.success == true && dataErp?.in_shop == true
    );
  }

  showNoLocationMessage() {
    const inventoryQuantity = this.querySelector('inventory-quantity');
    if (!inventoryQuantity) return;
    inventoryQuantity.innerHTML = `
      <div data-item-notification="true" class="inventory-locations__item item-not-available" role="listitem">
        <p class="inventory-locations__store-name">${this.dataset.messNotLocationsAvailable || 'Not locations available'}</p>
      </div>
    `;
  }

  getStorageKey(sku) {
    const productId = this.dataset.productId;

    return productId && sku ? `product-sku-${productId}-${sku}` : null;
  }

  async getData(erpInventoryUrl, storageKey) {
    const response = await fetch(`${erpInventoryUrl}`, {
      method: 'GET',
    });
    const data = await response.json();

    if (!data.success) return null;

    this.setStorageItem(storageKey, JSON.stringify(data));
    return data;
  }

  setStorageItem(key, value) {
    if (!key) return;

    const now = new Date();
    const timeToLive = 5 * 60 * 1000;

    const item = {
      value: value,
      expiry: now.getTime() + timeToLive,
    };


    localStorage.setItem(key, JSON.stringify(item));
  }

  getStorageItem(key) {
    if (!key) return null;

    const itemStr = localStorage.getItem(key);

    if (!itemStr) return null;

    try {
      const item = JSON.parse(itemStr);
      const now = new Date();
  
      if (item.expiry && now.getTime() > item.expiry) {
        localStorage.removeItem(key);
        return null;
      }
  
      return item.value !== undefined ? item.value : item;
    } catch (error) {
      return itemStr;
    }
  }

  removeStorageItem(key) {
    if (!key) return;
    localStorage.removeItem(key);
  }

  /**
   * Updates the inventory locations block.
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  updateInventoryLocations = (event) => {
    if(this.inventoryDataSource == 'erp') {
      const sku = event.detail?.resource?.sku;
      this.refreshLocation(sku);
    } else {
      if (event.detail.data.newProduct) {
        this.dataset.productId = event.detail.data.newProduct.id;
      } else if (event.target instanceof HTMLElement && event.target.dataset.productId !== this.dataset.productId) {
        return;
      }
      
      const newInventoryLocations = event.detail.data.html.querySelector('product-inventory-locations');
      if (!newInventoryLocations) return;
      morph(this, newInventoryLocations, { childrenOnly: true });
    }
  };

  renderDataLocation(dataErp) {
    const inventoryQuantity = this.querySelector('inventory-quantity');
    if (!inventoryQuantity) return;

    const mappedLocations = Array.isArray(dataErp?.mappedLocations) ? dataErp.mappedLocations : [];
    const notAvailableLabel = inventoryQuantity.dataset.notAvailableLabel || 'Not available';
    const amountLabel = inventoryQuantity.dataset.amountLabel || 'XXX units available';

    inventoryQuantity.innerHTML = mappedLocations.length
      ? mappedLocations.map((location) => this.createLocationMarkup(location, amountLabel, notAvailableLabel)).join('')
      : this.createEmptyLocationMarkup(notAvailableLabel);
  }

  clearDataLocation() {
    const inventoryQuantity = this.querySelector('inventory-quantity');
    if (!inventoryQuantity) return;
    inventoryQuantity.innerHTML = '';
  }

  createLocationMarkup(location, amountLabel, notAvailableLabel) {
    const id = location?.id || '';
    const name = location?.name || '';
    const address = location?.address || {};
    const city = address?.city || '';
    const postalCodeRaw = address?.zip || '';
    const addressLine = this.formatAddress(address);
    const locationDisplayAddress = addressLine || [city, postalCodeRaw].filter(Boolean).join(', ');
    const availableQty = Number(location?.UnrestrictStock || 0) - Number(location?.ReserveStock || 0);
    const status = this.getInventoryStatus(availableQty);
    const phone = location?.phone || address?.phone || '';

    return `
      <div class="inventory-locations__item" role="listitem" data-id="${this.escapeHtml(id)}" data-location-name="${this.escapeHtml(name)}" data-country="${this.escapeHtml(address?.country || '')}" data-postal-code="${this.escapeHtml(postalCodeRaw)}" data-latitude="${this.escapeHtml(location?.latitude || address?.latitude || location?.lat || '')}" data-longitude="${this.escapeHtml(location?.longitude || address?.longitude || location?.lng || '')}" data-available="${availableQty > 0}">
        <p class="inventory-locations__store-name">${this.escapeHtml(name)}</p>
        <div class="inventory-locations__address-row">
          <span class="inventory-locations__address-icon" aria-hidden="true">
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M9.23013 18.9046C9.35016 18.9689 9.48376 19 9.61528 19C9.74575 19 9.87935 18.9689 10.0004 18.9046C10.2969 18.7479 17.2308 15.0094 17.2308 8.32856C17.2308 4.2886 13.8135 1 9.61539 1C5.4173 1 2 4.2886 2 8.32856C1.99896 15.0063 8.93353 18.748 9.23013 18.9046ZM9.61424 2.54286C12.9303 2.54286 15.6264 5.13742 15.6264 8.32856C15.6264 13.2505 10.9962 16.4748 9.61424 17.3255C8.23019 16.4747 3.60209 13.2654 3.60209 8.32856C3.60209 5.13742 6.30141 2.54286 9.61424 2.54286Z" fill="black"/>
              <path d="M9.61486 12.0774C11.5229 12.0774 13.0764 10.5239 13.0764 8.61583C13.0764 6.70778 11.5229 5.1543 9.61486 5.1543C7.70681 5.1543 6.15332 6.70778 6.15332 8.61583C6.15332 10.5229 7.70781 12.0774 9.61486 12.0774ZM9.61486 6.69276C10.6766 6.69276 11.5379 7.55414 11.5379 8.61583C11.5379 9.67753 10.6766 10.5389 9.61486 10.5389C8.55317 10.5389 7.69178 9.67753 7.69178 8.61583C7.69178 7.55414 8.55617 6.69276 9.61486 6.69276Z" fill="black"/>
            </svg>
          </span>
          <span class="inventory-locations__address-text">${this.escapeHtml(locationDisplayAddress)}</span>
        </div>
        <div class="inventory-locations__meta-row">
          <span class="inventory-locations__status inventory-locations__status--${status.type}" style="--status-color: ${status.color};">
            <span class="inventory-locations__status-dot" aria-hidden="true"></span>
            <span class="inventory-locations__status-text">${this.escapeHtml(status.label)}</span>
          </span>
          ${phone
            ? `<a class="inventory-locations__phone" href="tel:${this.escapeHtml(this.normalizePhone(phone))}">
                <span class="inventory-locations__phone-icon" aria-hidden="true">
                  <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M13.4393 18C10.9084 17.9993 8.34074 16.7165 5.80912 14.1861C0.428377 8.80879 2.12414 4.58849 2.77048 3.42227C3.28175 2.50838 4.85871 2.14721 5.75538 2.01009C6.13795 1.95213 6.52052 2.14791 6.69872 2.49495L8.55784 6.11447C8.73816 6.46363 8.67027 6.89195 8.39166 7.17043L6.57144 8.98973C6.81682 9.56223 7.59257 10.6557 8.42843 11.5681C9.33995 12.4022 10.4346 13.1789 11.0088 13.4242L12.8283 11.6056C13.1077 11.3264 13.5348 11.2586 13.8863 11.4395L17.5076 13.297C17.8527 13.4751 18.0479 13.8553 17.9899 14.2398C17.8548 15.136 17.4927 16.7136 16.5784 17.2211C15.6626 17.7314 14.5779 18 13.4393 18Z" stroke="black" stroke-width="1.5"/>
                    <path d="M13.4393 18C10.9084 17.9993 8.34074 16.7165 5.80912 14.1861C0.428377 8.80879 2.12414 4.58849 2.77048 3.42227C3.28175 2.50838 4.85871 2.14721 5.75538 2.01009C6.13795 1.95213 6.52052 2.14791 6.69872 2.49495L8.55784 6.11447C8.73816 6.46363 8.67027 6.89195 8.39166 7.17043L6.57144 8.98973C6.81682 9.56223 7.59257 10.6557 8.42843 11.5681C9.33995 12.4022 10.4346 13.1789 11.0088 13.4242L12.8283 11.6056C13.1077 11.3264 13.5348 11.2586 13.8863 11.4395L17.5076 13.297C17.8527 13.4751 18.0479 13.8553 17.9899 14.2398C17.8548 15.136 17.4927 16.7136 16.5784 17.2211C15.6626 17.7314 14.5779 18 13.4393 18Z" stroke="black" stroke-opacity="0.2" stroke-width="1.5"/>
                  </svg>
                </span>
                <span class="inventory-locations__phone-text">${this.escapeHtml(phone)}</span>
              </a>`
            : ''}
        </div>
      </div>
    `;
  }

  createEmptyLocationMarkup(notAvailableLabel) {
    return `
      <div class="inventory-locations__item" role="listitem">
        <p class="inventory-locations__store-name">${this.escapeHtml(notAvailableLabel)}</p>
      </div>
    `;
  }

  getInventoryStatus(qty) {
    if (!qty || qty <= 0) return { type: 'out-stock', label: this.dataset.messOrderOfStock ?? 'Order of stock', color: '#A1A1A1' };
    if (qty >= 1 && qty <= 3) return { type: 'low-stock', label: this.dataset.messLowStock ?? 'Low Stock', color: '#FF780A' };
    return { type: 'in-stock', label: this.dataset.messInStock ?? 'In Stock', color: '#008000' };
  }

  normalizePhone(phone) {
    return String(phone || '').replace(/[^\d+]/g, '');
  }

  formatAddress(address) {
    if (!address) return '';
    const parts = [address.address1, address.address2, address.city, address.province, address.country, address.zip].filter(Boolean);
    return parts.join(', ');
  }

  searchLocations(query) {
    const inventoryQuantity = this.querySelector('inventory-quantity');
    if (!inventoryQuantity) return;
    const items = Array.from(inventoryQuantity.querySelectorAll('.inventory-locations__item'));
    const normalizedQuery = String(query || '').trim().toLowerCase();
    const queryTokens = normalizedQuery.split(/\s+/).filter(Boolean);

    items.forEach((item) => {
      const name = String(item.dataset.locationName || '').toLowerCase();
      const country = String(item.dataset.country || '').toLowerCase();
      const postalCode = String(item.dataset.postalCode || '').toLowerCase();
      const locationText = `${name} ${country} ${postalCode}`.replace(/\s+/g, ' ').trim();
      const matchesWholeQuery = !normalizedQuery || locationText.includes(normalizedQuery);
      const matchesAllTokens = !normalizedQuery || queryTokens.every((token) => locationText.includes(token));
      item.hidden = !(matchesWholeQuery || matchesAllTokens);
    });

    this.applyFilters();
  }

  async sortLocationsByGeocode(userCoords) {
    const inventoryQuantity = this.querySelector(
      'inventory-quantity'
    );
  
    if (!inventoryQuantity) {
      return;
    }
  
    const items = Array.from(
      inventoryQuantity.querySelectorAll(
        '.inventory-locations__item'
      )
    );
  
    const scoredItems = await Promise.all(
      items.map(async (item) => {
        const lat = Number(item.dataset.latitude);
        const lon = Number(item.dataset.longitude);
  
        const hasValidCoordinates =
        item.dataset.latitude !== '' &&
        item.dataset.longitude !== '' &&
        !Number.isNaN(lat) &&
        !Number.isNaN(lon);

        if (!hasValidCoordinates) {
          const locationZipcode = String(
            item.dataset.postalCode || ''
          ).trim();
        
          let distance =  this.getZipcodeDistanceScore(
            userCoords.zipcode,
            locationZipcode
          )
          return {
            item,
            distance: distance
          };
        }
  
        let distance = this.getDistanceBetweenCoords(
          userCoords.lat,
          userCoords.lon,
          lat,
          lon
        );

        return {
          item,
          distance: distance
        };
      })
    );
  
    scoredItems
      .sort((a, b) => a.distance - b.distance)
      .forEach(({ item }) => {
        inventoryQuantity.appendChild(item);
      });
  }

  getDistanceBetweenCoords(lat1, lon1, lat2, lon2) {
    const R = 6371;
  
    const toRad = (deg) => {
      return deg * (Math.PI / 180);
    };
  
    const dLat = toRad(lat2 - lat1);
    const dLon = toRad(lon2 - lon1);
  
    const a =
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos(toRad(lat1)) *
      Math.cos(toRad(lat2)) *
      Math.sin(dLon / 2) *
      Math.sin(dLon / 2);
  
    const c = 2 * Math.atan2(
      Math.sqrt(a),
      Math.sqrt(1 - a)
    );
  
    return R * c;
  }

  getZipcodeDistanceScore(userZipcode, locationZipcode) {
    const user = String(userZipcode || '').trim();
    const location = String(locationZipcode || '').trim();
  
    if (!user || !location) {
      return Number.MAX_SAFE_INTEGER;
    }
  
    if (user === location) {
      return 0;
    }
  
    let commonPrefix = 0;
    const maxLength = Math.min(user.length, location.length);
  
    for (let i = 0; i < maxLength; i++) {
      if (user[i] !== location[i]) {
        break;
      }
  
      commonPrefix++;
    }
  
    const prefixPenalty =
      (Math.max(user.length, location.length) - commonPrefix) * 100000;
  
    const numericDistance =
      Math.abs(Number(user) - Number(location));
  
    return prefixPenalty + numericDistance;
  }


  async geocodeLocation(query) {
    if (!query) return null;

    try {
      const response = await fetch(
        `https://nominatim.openstreetmap.org/search?format=jsonv2&limit=1&q=${encodeURIComponent(query)}`,
        {
          headers: {
            Accept: 'application/json',
          },
        }
      );
      const data = await response.json();
      const first = Array.isArray(data) ? data[0] : null;
      if (!first) return null;
      const lat = Number(first.lat);
      const lon = Number(first.lon);
      if (!Number.isFinite(lat) || !Number.isFinite(lon)) return null;
      return { lat, lon };
    } catch (error) {
      console.error('[product-inventory-locations] geocode location error', query, error);
      return null;
    }
  }

  applyFilters() {
    const inventoryQuantity = this.querySelector('inventory-quantity');
    if (!inventoryQuantity) return;
    const items = Array.from(inventoryQuantity.querySelectorAll('.inventory-locations__item'));
    const normalizedQuery = String(this.currentSearchQuery || '').trim().toLowerCase();
    const queryTokens = normalizedQuery.split(/\s+/).filter(Boolean);
    const onlyAvailable = Boolean(this.onlyAvailable);
    let itemAvailable = 0;

    items.forEach((item) => {
      const name = String(item.dataset.locationName || '').toLowerCase();
      const country = String(item.dataset.country || '').toLowerCase();
      const postalCode = String(item.dataset.postalCode || '').toLowerCase();
      const locationText = `${name} ${country} ${postalCode}`.replace(/\s+/g, ' ').trim();
      const matchesQuery = !normalizedQuery || locationText.includes(normalizedQuery) || queryTokens.every((token) => locationText.includes(token));
      const available = String(item.dataset.available || '').toLowerCase() === 'true';

      if(item.dataset.itemNotification === "true") {
        return;
      }

      if (!matchesQuery) {
        item.classList.add('hidden');
        return;
      }

      if (onlyAvailable && !available) {
        item.classList.add('hidden');
      } else {
        item.classList.remove('hidden');
        itemAvailable++;
      }
    });

    if(itemAvailable == 0) {
      if(inventoryQuantity.querySelector('.item-not-available')) {
          inventoryQuantity.querySelector('.item-not-available').classList.remove('hidden');
      } else {
          let elmentNotAvailable = document.createElement('div');
          elmentNotAvailable.innerHTML = `
          <div data-item-notification="true" class="inventory-locations__item  item-not-available" role="listitem">
            <p class="inventory-locations__store-name">${this.dataset.messNotLocationsAvailable || 'Not locations available'}</p>
          </div>
        `;
        inventoryQuantity.appendChild(elmentNotAvailable);
      }
    } else {
      if(inventoryQuantity.querySelector('.item-not-available')) {
        inventoryQuantity.querySelector('.item-not-available').classList.add('hidden');
      }
    }


  }

  escapeHtml(value) {
    return String(value ?? '').replace(/[&<>"]|'/g, (char) => ({
      '&': '&amp;',
      '<': '&lt;',
      '>': '&gt;',
      '"': '&quot;',
      "'": '&#39;'
    }[char]));
  }



}

if (!customElements.get('product-inventory-locations')) {
  customElements.define('product-inventory-locations', ProductInventoryLocations);
}
